import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Form from '../../../components/form/Form.component'
import type { FormValue, FormPage, FormSection, FormElement } from '../../../components/form/Form.types'

import { getFillablePDFsSelector } from '../selectors'
import { hydrateFillablePdfFromPcr, setCustomInputs } from '../store/fillablePDFs.actions'
import type { ICustomInput } from '../store/fillablePDFs.types'
import { inputTypeLookup } from '../store/fillablePDFs.types'
import { getFillablePDFOptionsSelector } from '../../options/selectors'
import { safeJsonParse } from '../../../utilities/safeParse.util'

const isEmpty = (v: any) => v == null || v === '' || (Array.isArray(v) && v.length === 0)

const Details: React.FC = () => {
  const dispatch = useDispatch()

  const fillablePDFsState = useSelector(getFillablePDFsSelector)
  const { customInputs, id } = fillablePDFsState

  const fillablePDFsOptionsState = useSelector(getFillablePDFOptionsSelector)
  const { metadata } = fillablePDFsOptionsState

  const fillablePDF = useMemo(() => {
    const row = metadata?.find((f: any) => f.FormNameWithId === id)
    if (!row?.Data) return null

    const parsed = safeJsonParse(row.Data)

    return parsed?.pages?.some((p: FormPage) => p.sections?.some((s: FormSection) => s.elements?.length > 0))
      ? parsed
      : null
  }, [metadata, id])

  const getFieldType = (ci: string): string => inputTypeLookup[ci] ?? 'text'

  const customInputsToFormValue = (inputs: ICustomInput[]): FormValue => {
    const formValue: FormValue = {}

    const grouped = inputs
      .filter((ci) => ci.fieldType !== 'signature')
      .reduce((acc, ci) => {
        ;(acc[ci.questionId] ||= []).push(ci)
        return acc
      }, {} as Record<string, ICustomInput[]>)

    for (const questionId of Object.keys(grouped)) {
      const entries = grouped[questionId]
      const isMultiSelect = entries[0].fieldType === inputTypeLookup.MultiSelect
      const answers = entries.map((e) => e.answerId)
      formValue[questionId] = isMultiSelect ? answers : answers.length === 1 ? answers[0] : answers
    }

    return formValue
  }

  const [form, setForm] = useState<FormValue>(() => (customInputs?.length ? customInputsToFormValue(customInputs) : {}))

  useEffect(() => {
    setForm(customInputs?.length ? customInputsToFormValue(customInputs) : {})
  }, [customInputs])

  useEffect(() => {
    if (!fillablePDF) return
    if (customInputs?.length) return

    dispatch(hydrateFillablePdfFromPcr(fillablePDF))
  }, [fillablePDF, customInputs?.length, dispatch])

  const handleFormChange = (nextForm: FormValue) => {
    setForm(nextForm)
    if (!fillablePDF) return

    const signatureInputs = (customInputs ?? []).filter((ci: ICustomInput) => ci.fieldType === 'signature')
    const inputs: ICustomInput[] = [...signatureInputs]

    const existingInputByQuestionId = (customInputs ?? []).reduce((acc, ci) => {
      acc[String(ci.questionId)] = ci
      return acc
    }, {} as Record<string, ICustomInput>)

    fillablePDF.pages?.forEach((page: FormPage) =>
      page.sections?.forEach((section: FormSection) =>
        section.elements?.forEach((element: FormElement) => {
          const elementId = element.id.toString()
          const value = nextForm[elementId]
          if (isEmpty(value)) return

          const fieldType = getFieldType(element.componentInterface)
          const existingInput = existingInputByQuestionId[elementId]
          const isDisabled = existingInput?.isDisabled === true

          if (Array.isArray(value)) {
            value.forEach((v) =>
              inputs.push({
                questionId: elementId,
                question: element.label,
                answerId: v,
                fieldType,
                isDisabled,
              })
            )
            return
          }

          inputs.push({
            questionId: elementId,
            question: element.label,
            answerId: value,
            fieldType,
            isDisabled,
          })
        })
      )
    )

    dispatch(setCustomInputs(inputs))
  }

  return (
    <>
      {fillablePDF && (
        <Form
          data={fillablePDF}
          value={form}
          onChange={handleFormChange}
          className='dynamic-form-section'
          filter={{ level: 'element', key: 'formId', value: id }}
          disabledFields={customInputs}
        />
      )}
    </>
  )
}

export default Details
